import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PipeActions } from '../redux-store/thunks/pipes.thunks';
import { SystemActions } from '../redux-store/thunks/systems.thunks';
import { DatasetActions } from '../redux-store/thunks/datasets.thunks';
import { leafPipesSelector } from '../redux-store/selectors';
import { getPipesCurlCommand } from '../utils/pipes.utils';

import { PipesListPage } from '../containers/PipesListPage.container';
import FilterTable from '../components/filter-table/FilterTable';
import { ListActions } from '../components/common/ListActions.view';

import {
  getPipesDefaultFilter,
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  emptyFilterPipes,
  filterLabelMapping,
} from '../constants/pipesListTableDefaults';
import { StorageKeys } from '../constants/storageKeys';

import { usePipesListColumns, PipesListContext } from '../hooks/usePipesListColumns.hook';
import { useLoadInternalPipes } from '../hooks/useLoadInternalPipes.hook';
import useLocalStorage from '../hooks/useLocalStorage';

import type { RootState } from '../types/state.types';
import type { Pipe } from '../types/pipes.types';

export const InternalPipesContainer: React.FC = () => {
  const dispatch = useDispatch();

  const [showBytesExact, setShowBytesExact] = useLocalStorage(
    StorageKeys.dataTable,
    ['internalPipes', 'showBytesExact'],
    true
  );

  const toggleShowBytesExact = () => {
    setShowBytesExact(!showBytesExact);
  };

  const internalPipes = useSelector((state: RootState) => state.internalEntities.pipes);
  const leafPipes = useSelector((state: RootState) => leafPipesSelector(state));
  const datasets = useSelector((state: RootState) => state.datasets);
  const systems = useSelector((state: RootState) => state.systems);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);

  const columns = usePipesListColumns(leafPipes, datasets, systems);

  const [shouldLoadDatasets, setShouldLoadDataset] = useState(false);

  const getSelectedPipes = (selectedIds: string[]) =>
    selectedIds?.map((id) => internalPipes.find((p) => p._id === id))?.filter(Boolean) as Pipe[];

  const loadSystems = (loadInternal?: boolean) => {
    dispatch(SystemActions.loadAllForced(loadInternal));
  };

  const loadDatasets = (loadInternal?: boolean) => {
    dispatch(DatasetActions.loadAll(loadInternal));
  };

  const onDelete = (selectedIds: string[]) => dispatch(PipeActions.delete(selectedIds));

  const handleVisibleColsChanged = useCallback(
    (newCols: string[]) => {
      // in case that systems or datasets are not loaded already, unlikely
      if ((newCols.includes('From') || newCols.includes('To')) && !systems) {
        loadSystems();
      }
      if (
        newCols.includes('Populated') ||
        newCols.includes('Index w/o deleted') ||
        newCols.includes('Complete index') ||
        newCols.includes('Complete log')
      ) {
        if (!datasets) {
          loadDatasets();
        }

        setShouldLoadDataset(true);
      } else {
        setShouldLoadDataset(false);
      }
    },
    [loadSystems, loadDatasets, datasets, systems]
  );

  useLoadInternalPipes(subUrl, token);

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedPipes = getSelectedPipes(selectedIds);
    const deletePipes = () => {
      onDelete(selectedIds);
      clearSelection();
    };

    return <ListActions selection={selectedPipes} onDelete={deletePipes} name="pipe" />;
  };

  return (
    <PipesListContext.Provider
      value={{
        datasets,
        systems,
        showBytesExact,
        toggleShowBytesExact,
      }}
    >
      <PipesListPage
        includeInternal={true}
        shouldLoadDatasets={shouldLoadDatasets}
        shouldRenderRefreshButton="render"
      >
        <FilterTable
          actionsRenderer={renderActions}
          defaults={{
            filter: getPipesDefaultFilter('system'),
            sortBy: defaultSortBy,
            visibleCols: defaultVisibleCols,
            visibleFacets: defaultVisibleFacets,
          }}
          emptyFilter={emptyFilterPipes}
          filterLabelMapping={filterLabelMapping}
          columns={columns}
          itemsToFilter={internalPipes}
          tableKey="internalPipes"
          onVisibleColsChanged={handleVisibleColsChanged}
          curl={getPipesCurlCommand(subUrl, token, true)}
        />
      </PipesListPage>
    </PipesListContext.Provider>
  );
};
